import React, { ChangeEvent, useContext } from "react";
import IPrice_Group from "../../../../../models/IPrice_Group";
import * as _ from "lodash-es";
import SupplierContext from "../../../../../contexts/SupplierContext";
import OwLabel from "../../../../shared/OwLabel/OwLabel";
import { NumericFormat } from "react-number-format";
import { FormattedNumber } from "react-intl";
import { OwFnInput } from "../../../../shared/OwFnInput/OwFnInput";
import OwCheckbox from "../../../../shared/OwCheckbox/OwCheckbox";
import ICatalog from "../../../../../models/ICatalog";
import IPrice_Bands_Structured from "../../../../../models/IPrice_Bands_Structured";
import { CatalogItemPriceBandBuilder } from "../CatalogItemPriceBandBuilder/CatalogItemPriceBandBuilder";
import ICatalog_Item from "../../../../../models/ICatalog_Item";
import ICatalog_ItemUI from "../../../../../models/ICatalog_ItemUI";
import IPrice_Band_Compiled from "../../../../../models/IPrice_Band_Compiled";
import IVendor_Source from "../../../../../models/IVendor_Source";
import OwToggle from "@components/shared/OwToggle/OwToggle";

type Props = {
    catalogModel: ICatalog;
    catalogItem?: ICatalog_ItemUI;
    hasPriceBands: boolean;
    priceBands: IPrice_Bands_Structured;
    pricing: { [key: string]: number | "N/A" };
    pricingDescriptions: { [key: string]: string };
    priceGroups?: IPrice_Group[];
    costPerUom?: number;
    /**
     * Supply a function to update the state pricing for the item
     *
     * @param pricing
     */
    handleUpdatePricing: (pricing: { [key: string]: number | "N/A" }) => void;
    /**
     * Supply a function to update the state pricing description for the item
     *
     * @param pricing
     */
    handleDescriptionUpdate: (pricingDescription: { [key: string]: string }) => void;
    handleBandUpdate: (band: IPrice_Band_Compiled[], priceGroup?: IPrice_Group, vendorSource?: IVendor_Source) => void;
};

export const CatalogItemSidebarPriceGroupInputs = (props: Props) => {
    const supplierModel = useContext(SupplierContext);

    const handleChange = (e: ChangeEvent<any>) => {
        const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        const name = e.target.name;

        const pricingUpdate = {
            ...props.pricing,
        };

        // OwToggle sends boolean (true = checked/active, false = unchecked)
        // When toggle is ON (true), set to "N/A", when OFF (false), set to 0
        pricingUpdate[name] = value === true || value === 1 ? "N/A" : value === false || value === 0 ? 0 : value;

        props.handleUpdatePricing(pricingUpdate);
    };

    const handlePricingDescriptionChange = (e: ChangeEvent<any>) => {
        const value = e.target.value;
        const name = e.target.name;

        const pricingDescriptionUpdate = {
            ...props.pricingDescriptions,
        };

        pricingDescriptionUpdate[name] = value;

        props.handleDescriptionUpdate(pricingDescriptionUpdate);
    };

    return (
        <div className={"CatalogItemSidebarPriceGroupInputs"}>
            {(props.priceGroups || []).map((priceGroup, index) => {
                const name = `pricing['${priceGroup.value}']`;
                const pricingDescription = `pricing_descriptions['${priceGroup.value}']`;

                const value = Number(_.get(props.pricing, `${priceGroup.value}`, 0));
                const descriptionValue = _.get(props.pricingDescriptions, `${priceGroup.value}`, "");

                // make sure the current state.pricing is setup.
                if (!props.pricing[`${priceGroup.value}`]) {
                    props.pricing[`${priceGroup.value}`] = 0;
                }

                let profitPer = 0;
                let profitMargin = 0;
                let profitClassName = "text-success";

                if (!_.isNil(props.costPerUom) && supplierModel.has_cost_pricing) {
                    if (value.toString() !== "N/A" && typeof value === "number") {
                        const cost = Number(props.costPerUom);

                        profitPer = value - cost;
                        const grossProfit = value - cost;
                        profitMargin = (grossProfit / value) * 100;

                        if (profitMargin < 0) {
                            profitClassName = "text-danger";
                        } else if (profitMargin > 0) {
                            profitClassName = "text-success";
                        } else {
                            profitClassName = "text-warning";
                        }
                    }
                }

                return (
                    <div key={index} className={"mb-4"}>
                        <OwLabel
                            isBold={true}
                            title={priceGroup.value}
                            noBottomMargin={true}
                            skipTranslate={true}
                            leftIconClass={"fad fa-tag"}
                        />
                        {_.isNumber(Number(value)) && value.toString() !== "N/A" ? (
                            <div>
                                {props.hasPriceBands ? (
                                    <CatalogItemPriceBandBuilder
                                        handleBandUpdate={props.handleBandUpdate}
                                        catalogId={props.catalogModel.id}
                                        band={props.priceBands[priceGroup.value] || []}
                                        priceGroup={priceGroup}
                                        catalogItem={props.catalogItem}
                                    />
                                ) : (
                                    <NumericFormat
                                        value={value}
                                        onChange={handleChange}
                                        name={priceGroup.value}
                                        className="form-control form-control-sm"
                                    />
                                )}
                                {props.costPerUom ? (
                                    <div>
                                        <OwLabel title={"profit"} noBottomMargin={true} className={"me-1"} />
                                        <span className={profitClassName}>
                                            <FormattedNumber value={profitPer} maximumFractionDigits={2} />
                                        </span>

                                        <OwLabel title={"margin"} noBottomMargin={true} className={"me-1 ms-5"} />
                                        <span className={profitClassName}>
                                            <FormattedNumber value={profitMargin} maximumFractionDigits={2} />%
                                        </span>
                                    </div>
                                ) : null}
                                {supplierModel.has_pricing_descriptions ? (
                                    <div>
                                        <div>
                                            <OwLabel title={"description"} />
                                        </div>
                                        <div>
                                            <OwFnInput
                                                fn={handlePricingDescriptionChange}
                                                name={priceGroup.value}
                                                value={descriptionValue}
                                            />
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        ) : (
                            <div>N/A</div>
                        )}
                        <OwToggle
                            wrapperClassName="mt-2"
                            label={"not_available"}
                            name={priceGroup.value}
                            active={value.toString() === "N/A"}
                            fn={handleChange}
                            labelOnRight={true}
                        />
                        <hr />
                    </div>
                );
            })}
        </div>
    );
};
